import { Box, IconButton, Modal, Stack, Typography, useTheme } from '@mui/material';
import { CommunityPost, PostCategory } from '../../../database/community';
import CommentEditor from '../comment/CommentEditor';
import CloseIcon from '@mui/icons-material/Close';
import { getConfig } from '../../../config';
import { useEffect, useRef } from 'react';
import UserHeader from '../../../components/UserHeader';
import { useCache } from '../../../api/cache/Cache';
import { useApi } from '../../../api/Api';
import { useRequest } from '../../../api/Request';
import AudioPlayer from '../audio/AudioPlayer';
import VideoPlayer from '../video/VideoPlayer';
import { SubscriptionStatus } from '../../../database/user';
import MentionText from '../../../components/MentionText';
import { motion } from 'framer-motion';

interface CommunityPostModalProps {
    post: CommunityPost;
    isModalOpen: boolean;
    handleCloseModal: () => void;
    onSave: (post: CommunityPost) => void;
}

const CommunityPostModal: React.FC<CommunityPostModalProps> = ({
    post,
    isModalOpen,
    handleCloseModal,
    onSave,
}) => {
    const theme = useTheme();
    const communityImagesBucket = getConfig().media.communityImagesBucket;
    const lastCommentRef = useRef<HTMLDivElement | null>(null);
    const cache = useCache();
    const api = useApi();
    const getUsersBatchRequest = useRequest();
    const prevOpenRef = useRef(false);

    // Scroll to the last comment only when the modal first opens
    useEffect(() => {
        if (isModalOpen && !prevOpenRef.current) {
            // Use a short timeout to let the modal render before scrolling
            setTimeout(() => {
                lastCommentRef.current?.scrollIntoView({ behavior: 'smooth' });
            }, 100);
        }
        prevOpenRef.current = isModalOpen;
    }, [isModalOpen]);

    // Fetch missing user summaries for commenters
    useEffect(() => {
        if (!isModalOpen) return;

        const uniqueUsernames = Array.from(
            new Set(
                post.comments.flatMap((comment) => {
                    return [comment.owner];
                }),
            ),
        );

        const missingUsernames = uniqueUsernames.filter(
            (username) => !cache.userSummaries.get(username),
        );

        if (missingUsernames.length > 0) {
            api.getUsersBatch(missingUsernames)
                .then((getUsersBatchResponse) => {
                    const fetchedUsers = getUsersBatchResponse.data.users;
                    cache.userSummaries.putMany(fetchedUsers);
                    getUsersBatchRequest.onSuccess('Successfully fetched users');
                })
                .catch((err) => {
                    console.error('Fetch users for requests error: ', err);
                    getUsersBatchRequest.onFailure(err);
                });
        }
    }, [isModalOpen, post, api, cache.userSummaries, getUsersBatchRequest]);

    return (
        <Modal
            open={isModalOpen}
            onClose={handleCloseModal}
            slotProps={{
                backdrop: {
                    sx: {
                        backdropFilter: 'blur(4px)',
                        backgroundColor: 'rgba(0, 0, 0, 0.65)',
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '90%', md: '40%' },
                    outline: 'none',
                }}
            >
            <motion.div
                initial={{ opacity: 0, y: 48 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 28 }}
            >
            <Stack
                sx={{
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    maxHeight: '600px',
                    backgroundColor: `${theme.palette.background.default}`,
                    boxShadow: '0 24px 64px rgba(0,0,0,0.85), 0 0 0 1px rgba(139, 0, 0, 0.12)',
                }}
            >
                <Stack direction='row' padding='10px' alignItems='center'>
                    <UserHeader
                        timestamp={post.createdAt}
                        username={post.owner}
                        displayName={cache.userSummaries.get(post.owner)?.displayName}
                        isArtist={cache.userSummaries.get(post.owner)?.isArtist}
                        subscriptionStatus={
                            cache.userSummaries.get(post.owner)?.subscriptionStatus ||
                            SubscriptionStatus.FREE_TIER
                        }
                        subscriptionOverride={
                            cache.userSummaries.get(post.owner)?.subscriptionOverride
                        }
                        isEdited={post.updatedAt !== post.createdAt}
                    />
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            marginLeft: 'auto',
                            color: `${theme.palette.secondary.main}`,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
                <Box
                    sx={{
                        overflow: 'auto',
                    }}
                >
                    <Box sx={{ padding: '10px', paddingTop: '0px' }}>
                        <div
                            style={{
                                marginBottom: '10px',
                            }}
                        >
                            <MentionText
                                variant='body2'
                                sx={{
                                    padding: '5px',
                                    paddingTop: '0px',
                                    overflowWrap: 'break-word',
                                }}
                            >
                                {post.content}
                            </MentionText>
                            {post.containsMedia && (
                                <Box
                                    sx={{
                                        width: '100%',
                                        maxHeight: '500px',
                                        overflow: 'hidden',
                                        borderRadius: '16px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        position: 'relative',
                                    }}
                                >
                                    {post.category === PostCategory.MUSIC ? (
                                        <AudioPlayer
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                        />
                                    ) : post.category === PostCategory.VIDEO ? (
                                        <Box
                                            sx={{
                                                width: '100%',
                                                maxHeight: '500px',
                                                '& video': {
                                                    maxHeight: '500px !important',
                                                },
                                            }}
                                        >
                                            <VideoPlayer
                                                src={
                                                    post.previewUrl ??
                                                    `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                                }
                                            />
                                        </Box>
                                    ) : post.category === PostCategory.PHOTO ? (
                                        <img
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                            style={{
                                                width: '100%',
                                                maxHeight: '500px',
                                                height: 'auto',
                                                objectFit: 'contain',
                                            }}
                                            alt={`${post.ownerDisplayName}'s media`}
                                        />
                                    ) : (
                                        <img
                                            src={
                                                post.previewUrl ??
                                                `${communityImagesBucket}/${post.owner}/${post.createdAt}`
                                            }
                                            style={{
                                                width: '100%',
                                                maxHeight: '500px',
                                                height: 'auto',
                                                objectFit: 'contain',
                                            }}
                                            alt={`${post.ownerDisplayName}'s post`}
                                        />
                                    )}
                                </Box>
                            )}
                        </div>
                        {post.comments.length > 0 &&
                            post.comments.map((comment, i) => (
                                <Box
                                    key={comment.owner + comment.createdAt}
                                    ref={
                                        i === post.comments.length - 1
                                            ? lastCommentRef
                                            : null
                                    }
                                    sx={{
                                        padding: '5px',
                                        background: `${theme.palette.secondary.light}`,
                                        borderRadius: '16px',
                                        mb:
                                            post.comments.length - 1 === i
                                                ? '0px'
                                                : '20px',
                                    }}
                                >
                                    <UserHeader
                                        timestamp={comment.createdAt}
                                        username={comment.owner}
                                        displayName={
                                            cache.userSummaries.get(comment.owner)
                                                ?.displayName
                                        }
                                        isArtist={
                                            cache.userSummaries.get(comment.owner)
                                                ?.isArtist
                                        }
                                        subscriptionStatus={
                                            cache.userSummaries.get(comment.owner)
                                                ?.subscriptionStatus ||
                                            SubscriptionStatus.FREE_TIER
                                        }
                                        subscriptionOverride={
                                            cache.userSummaries.get(comment.owner)
                                                ?.subscriptionOverride
                                        }
                                    />
                                    <MentionText
                                        variant='body2'
                                        sx={{
                                            overflowWrap: 'break-word',
                                            padding: '4px',
                                        }}
                                    >
                                        {post.comments[i].content}
                                    </MentionText>
                                </Box>
                            ))}
                    </Box>
                </Box>
                <CommentEditor post={post} onSave={onSave} />
            </Stack>
            </motion.div>
            </Box>
        </Modal>
    );
};
export default CommunityPostModal;
